import { UserRole } from "@edlab-fms/shared";
import { useState } from "react";
import { useAuthQuery } from "../../lib/api/auth";
import { useFiscalDefaults } from "../../lib/hooks/useFiscalDefaults";
import { useUnitMovementsQuery, type ProgrammeUnitSummary } from "../../lib/api/overview";
import { ApiClientError } from "../../lib/api/client";
import { useUpdateSettingsUnitMutation } from "../../lib/api/settings";
import { formatMoney } from "../../lib/format/money";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/ui/Table";
import { EmptyState } from "../../components/ui/EmptyState";

interface ProgrammeUnitDetailModalProps {
  unit: ProgrammeUnitSummary;
  period: string;
  onClose: () => void;
}

type ModalView = "detail" | "edit-budget";

export function ProgrammeUnitDetailModal({
  unit,
  period,
  onClose,
}: ProgrammeUnitDetailModalProps) {
  const auth = useAuthQuery();
  const { fiscalYearName } = useFiscalDefaults();
  const canEditBudget = auth.data?.role === UserRole.SUPER_ADMIN;
  const movements = useUnitMovementsQuery(unit.id, period);
  const updateUnit = useUpdateSettingsUnitMutation();

  const [view, setView] = useState<ModalView>("detail");
  const [annualBudget, setAnnualBudget] = useState(unit.annualBudget);
  const [budgetInput, setBudgetInput] = useState(unit.annualBudget);
  const [budgetError, setBudgetError] = useState<string | null>(null);

  async function saveBudget() {
    setBudgetError(null);
    const parsed = Number.parseFloat(budgetInput);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setBudgetError("Enter a valid budget amount.");
      return;
    }

    try {
      const result = await updateUnit.mutateAsync({
        id: unit.id,
        annualBudget: parsed.toFixed(2),
      });
      setAnnualBudget(result.unit.annualBudget);
      setView("detail");
    } catch (err) {
      setBudgetError(err instanceof ApiClientError ? err.message : "Could not save budget.");
    }
  }

  if (view === "edit-budget") {
    return (
      <Modal
        open
        onClose={() => {
          setBudgetError(null);
          setView("detail");
        }}
        title="Annual budget"
        description={`${unit.name}${fiscalYearName ? ` · ${fiscalYearName}` : ""}`}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setBudgetError(null);
                setView("detail");
              }}
            >
              Cancel
            </Button>
            <Button disabled={updateUnit.isPending} onClick={() => void saveBudget()}>
              Save budget
            </Button>
          </>
        }
      >
        {budgetError ? <p className="edlab-error mb-4">{budgetError}</p> : null}
        <label className="form-field">
          Budget · PKR
          <input
            type="number"
            min="0"
            step="0.01"
            className="edlab-input"
            value={budgetInput}
            onChange={(event) => {
              setBudgetInput(event.target.value);
            }}
          />
        </label>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={unit.name}
      description={unit.groupName}
      footer={
        <>
          {canEditBudget ? (
            <Button
              variant="secondary"
              onClick={() => {
                setBudgetInput(annualBudget);
                setBudgetError(null);
                setView("edit-budget");
              }}
            >
              Edit annual budget
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid gap-4 rounded-[11px] bg-[#f5f8f6] p-[18px] sm:grid-cols-3">
          <div>
            <small className="block text-xs text-[#84908d]">Annual budget</small>
            <b className="mt-1.5 block text-base font-semibold tracking-[-0.3px] text-edlab-ink">
              {formatMoney(annualBudget)}
            </b>
          </div>
          <div>
            <small className="block text-xs text-[#84908d]">YTD income</small>
            <b className="mt-1.5 block text-base font-semibold tracking-[-0.3px] text-edlab-ink">
              {formatMoney(unit.ytdIncome ?? "0")}
            </b>
          </div>
          <div>
            <small className="block text-xs text-[#84908d]">YTD paid</small>
            <b className="mt-1.5 block text-base font-semibold tracking-[-0.3px] text-edlab-ink">
              {formatMoney(unit.ytdPaid ?? "0")}
            </b>
          </div>
        </div>

        <div>
          <h3 className="mb-3 font-semibold text-edlab-ink">Posted movements</h3>
          {movements.isLoading ? (
            <LoadingSpinner label="Loading movements…" />
          ) : movements.data?.movements.length ? (
            <Table caption="Unit movements">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Date</TableHeaderCell>
                  <TableHeaderCell>Voucher</TableHeaderCell>
                  <TableHeaderCell>Description</TableHeaderCell>
                  <TableHeaderCell className="text-right">Amount</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.data.movements.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>{row.voucher}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.kind === "income" ? "+" : "−"}
                      {formatMoney(row.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <EmptyState
              title="No posted movements"
              description="Transactions for this unit will appear here once receipts or payments are recorded."
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
